import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { getCurrentUser, signOut, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { Hub } from 'aws-amplify/utils';
import { GET_CURRENT_USER } from '../graphql/queries';

const client = generateClient();

type UserRole = 'ADMIN' | 'USER';

interface User {
  userId: string;
  email: string;
  name?: string;
  role?: UserRole;
}

interface GetCurrentUserResponse {
  getCurrentUser: {
    userId: string;
    email: string;
    role: UserRole;
  } | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  signIn: () => void;
  signOut: () => Promise<void>;
  refreshUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserRole = useCallback(async (userId: string, email: string, name?: string) => {
    try {
      const response = await client.graphql({
        query: GET_CURRENT_USER,
      }) as GraphQLResult<GetCurrentUserResponse>;

      const userData = response.data?.getCurrentUser;
      setUser({
        userId,
        email,
        name,
        role: userData?.role || 'USER',
      });
    } catch (err) {
      console.error('Error fetching user role:', err);
      // Default to USER role if we can't fetch
      setUser({
        userId,
        email,
        name,
        role: 'USER',
      });
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken;
      const claims = idToken?.payload;

      const email = claims?.email as string || '';
      const name = claims?.name as string;

      // Fetch user role from our API
      await fetchUserRole(currentUser.userId, email, name);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUserRole]);

  useEffect(() => {
    // Check for OAuth callback code in URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.has('code');

    if (hasCode) {
      // Amplify will automatically handle the code exchange
      // Clear the URL params after processing
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    checkAuth();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'signInWithRedirect':
          checkAuth();
          break;
        case 'signedOut':
          setUser(null);
          break;
        case 'signInWithRedirect_failure':
          console.error('OAuth error:', payload.data);
          setIsLoading(false);
          break;
      }
    });

    return unsubscribe;
  }, [checkAuth]);

  async function handleSignIn() {
    try {
      // Use Amplify's signInWithRedirect with custom provider for SAML
      const identityProvider = import.meta.env.VITE_IDENTITY_PROVIDER || 'AzureAD';
      await signInWithRedirect({
        provider: { custom: identityProvider },
      });
    } catch (error) {
      console.error('Error signing in:', error);
    }
  }

  async function handleSignOut() {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  async function refreshUserRole() {
    if (user) {
      await fetchUserRole(user.userId, user.email, user.name);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'ADMIN',
        signIn: handleSignIn,
        signOut: handleSignOut,
        refreshUserRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
